import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { isToday, isYesterday, isThisWeek, isThisMonth } from 'date-fns';
import Button from '../../../shared/Button';
import Text from '../../../shared/Text';

const groupConversations = conversations => {
  const groupedConversations = {
    Today: [],
    Yesterday: [],
    'This Week': [],
    'This Month': [],
  };
  conversations.forEach(conversation => {
    const date = new Date(conversation.createdAt);

    if (isToday(date)) {
      groupedConversations.Today = [
        ...groupedConversations.Today,
        conversation,
      ];
    } else if (isYesterday(date)) {
      groupedConversations.Yesterday = [
        ...groupedConversations.Yesterday,
        conversation,
      ];
    } else if (isThisWeek(date)) {
      groupedConversations['This Week'] = [
        ...groupedConversations['This Week'],
        conversation,
      ];
    } else if (isThisMonth(date)) {
      groupedConversations['This Month'] = [
        ...groupedConversations['This Month'],
        conversation,
      ];
    } else {
      const formattedMonth = new Intl.DateTimeFormat('en-US', {
        month: 'long',
      }).format(date);
      const conversationsByMonth = groupedConversations[formattedMonth] || [];

      groupedConversations[formattedMonth] = [
        ...conversationsByMonth,
        conversation,
      ];
    }
  });
  return groupedConversations;
};

const HistoryGroup = ({
  title,
  conversations,
  setConversation,
  activeConversationId,
}) => {
  return (
    <GroupContainer>
      <Conversation>
        <Text variant="body1">{title}</Text>
      </Conversation>
      {conversations.map(conversation => (
        <Conversation key={conversation.id}>
          <ConversationButton
            onClick={() => {
              setConversation(conversation);
            }}
            mode="text"
            variant="black"
            width="auto"
            padding="4px"
            isActive={activeConversationId === conversation.id}
          >
            {conversation.lastUserMessage}
          </ConversationButton>
        </Conversation>
      ))}
    </GroupContainer>
  );
};

export const ChatHistory = ({
  isSidebarOpen,
  conversations,
  setConversation,
  activeConversationId,
}) => {
  const groupedConversations = useMemo(
    () => groupConversations(conversations),
    [conversations],
  );

  return (
    <Container isSidebarOpen={isSidebarOpen}>
      {Object.keys(groupedConversations).map(
        key =>
          groupedConversations[key].length > 0 && (
            <HistoryGroup
              key={key}
              title={key}
              conversations={groupedConversations[key]}
              setConversation={setConversation}
              activeConversationId={activeConversationId}
            />
          ),
      )}
    </Container>
  );
};

ChatHistory.propTypes = {
  isSidebarOpen: PropTypes.bool,
  conversations: PropTypes.array,
  setConversation: PropTypes.func,
  activeConversationId: PropTypes.number,
};

HistoryGroup.propTypes = {
  title: PropTypes.string,
  conversations: PropTypes.array,
  setConversation: PropTypes.func,
  activeConversationId: PropTypes.number,
};

const Container = styled.ul`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${props =>
    `rgb(from ${props.theme.colors.primary[10]} r g b / 0.3)`};
  transition: width 0.4s ease-in-out;
  transition-delay: 0s;
  border-radius: 0 0 0 8px;
  padding-top: 8px;

  gap: 16px;
  overflow-x: hidden;
  overflow-y: auto;
  justify-self: flex-start;

  ${props =>
    props.isSidebarOpen
      ? css`
          width: 250px;
        `
      : css`
          width: 0;
        `}
`;

const Conversation = styled.li`
  padding: 4px 8px;
  width: 230px;
`;

const GroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ConversationButton = styled(Button)`
  white-space: nowrap;
  text-align: left;
  text-overflow: ellipsis;
  overflow: hidden;
  width: 230px;
  padding: 4px 8px;
  height: auto;

  ${props =>
    props.isActive &&
    css`
      color: ${props.theme.colors.black[100]};
      background-color: ${props.theme.colors.primary[20]} !important;
    `}

  &:hover {
    color: ${props => props.theme.colors.black[100]};
    background-color: ${props => props.theme.colors.primary[20]};
  }

  &:focus {
    color: ${props => props.theme.colors.black[100]};
    background-color: transparent;
  }

  &:active {
    color: ${props => props.theme.colors.black[120]};
    background-color: ${props => props.theme.colors.primary[20]};
  }
`;
